import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { marketplaceListingService, cardService } from '../services/api';

interface BundleItem {
  name: string;
  quantity: number;
  notes?: string;
  cardId?: string;
  imageUrl?: string;
}

const EditListingPage = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [fetchingListing, setFetchingListing] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Form fields
  const [price, setPrice] = useState('');
  const [condition, setCondition] = useState('mint');
  const [quantity, setQuantity] = useState('1');
  const [description, setDescription] = useState('');
  const [cardName, setCardName] = useState('');
  const [isBundle, setIsBundle] = useState(false);
  const [bundleName, setBundleName] = useState('');
  const [bundleItems, setBundleItems] = useState<BundleItem[]>([]);
  const [bundleSearchTerm, setBundleSearchTerm] = useState('');
  const [bundleSearchResults, setBundleSearchResults] = useState<Array<{ id: string; name: string; imageUrl?: string; cardNumber?: string }>>([]);
  const [bundleSearching, setBundleSearching] = useState(false);

  const parseBundleItems = (raw: any): BundleItem[] => {
    if (!raw) return [];
    if (Array.isArray(raw)) return raw as BundleItem[];
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  };

  const handleBundleItemChange = (index: number, field: keyof BundleItem, value: string | number) => {
    setBundleItems((items) =>
      items.map((item, i) =>
        i === index
          ? {
            ...item,
            [field]: field === 'quantity' ? Math.max(1, Number(value) || 1) : value
          }
          : item
      )
    );
  };

  const handleRemoveBundleItem = (index: number) => {
    setBundleItems((items) => items.filter((_, i) => i !== index));
  };

  const handleAddBundleCard = (card: { id: string; name: string; imageUrl?: string }) => {
    setBundleItems((items) => [
      ...items,
      { name: card.name, quantity: 1, cardId: String(card.id), imageUrl: card.imageUrl }
    ]);
    setBundleSearchTerm('');
    setBundleSearchResults([]);
  };

  const handleAddCustomBundleItem = () => {
    const name = bundleSearchTerm.trim();
    if (!name) return;
    setBundleItems((items) => [...items, { name, quantity: 1 }]);
    setBundleSearchTerm('');
    setBundleSearchResults([]);
  };

  useEffect(() => {
    if (!isBundle) return;
    const term = bundleSearchTerm.trim();
    if (term.length < 2) {
      setBundleSearchResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        setBundleSearching(true);
        const response = await cardService.getCards({ search: term, limit: 8 });
        setBundleSearchResults(response.data.data || []);
      } catch (err) {
        console.error('Failed to search cards for bundle item', err);
        setBundleSearchResults([]);
      } finally {
        setBundleSearching(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [bundleSearchTerm, isBundle]);

  useEffect(() => {
    fetchListingDetails();
  }, [id]);

  const fetchListingDetails = async () => {
    try {
      setFetchingListing(true);
      const response = await marketplaceListingService.getListingById(id!);
      const listing = response.data.data;

      // Check if user owns this listing
      const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
      if (currentUser.id !== listing.sellerId) {
        setError('You can only edit your own listings');
        setTimeout(() => navigate(`/marketplace/listing/${id}`), 2000);
        return;
      }

      setPrice(listing.price.toString());
      setCondition(listing.condition);
      setQuantity(listing.quantity.toString());
      setDescription(listing.description || '');
      setIsBundle(Boolean(listing.isBundle));
      setBundleName(listing.bundleName || '');
      const parsedBundleItems = parseBundleItems(listing.bundleItems);
      setBundleItems(parsedBundleItems.length > 0 ? parsedBundleItems : []);
      setBundleSearchResults([]);
      setCardName(
        listing.isBundle
          ? listing.bundleName || 'Bundle Listing'
          : listing.card?.name || listing.customCardName || 'Unknown Card'
      );

      setError(null);
    } catch (error: any) {
      console.error('Failed to fetch listing details:', error);
      setError(t('editListing.failedToLoad', 'Failed to load listing details. Please try again.'));
    } finally {
      setFetchingListing(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const normalizedBundleItems = bundleItems
      .map((item) => ({
        name: item.name.trim(),
        quantity: Math.max(1, Number(item.quantity) || 1),
        notes: item.notes?.trim() || undefined,
        cardId: item.cardId ? Number(item.cardId) : undefined
      }))
      .filter((item) => item.name);

    if (isBundle && normalizedBundleItems.length === 0) {
      setError(t('createListing.bundleItemsRequired', 'Please add at least one card to the bundle'));
      return;
    }

    // Validation
    if (!price || parseFloat(price) <= 0) {
      setError(t('createListing.invalidPrice', 'Please enter a valid price'));
      return;
    }

    if (!quantity || parseInt(quantity) <= 0) {
      setError(t('createListing.invalidQuantity', 'Please enter a valid quantity'));
      return;
    }

    try {
      setLoading(true);

      const formData = new FormData();
      formData.append('price', price);
      formData.append('condition', condition); // Current condition logic
      formData.append('quantity', quantity);
      formData.append('isBundle', String(isBundle));
      if (isBundle) {
        if (bundleName.trim()) {
          formData.append('bundleName', bundleName.trim());
        }
        formData.append('bundleItems', JSON.stringify(normalizedBundleItems));
      }
      if (description.trim()) {
        formData.append('description', description);
      }

      await marketplaceListingService.updateListing(id!, formData);

      // Redirect back to listing detail immediately
      navigate(`/marketplace/listing/${id}`);
    } catch (error: any) {
      console.error('Failed to update listing:', error);
      setError(error.response?.data?.message || t('editListing.failedToUpdate', 'Failed to update listing. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  if (fetchingListing) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">{t('common.loading', 'Loading listing...')}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate(`/marketplace/listing/${id}`)}
          className="text-blue-600 hover:text-blue-700 mb-4 flex items-center"
        >
          ← {t('editListing.backToListing', 'Back to Listing')}
        </button>
        <h1 className="text-4xl font-bold mb-2">{t('editListing.title', 'Edit Listing')}</h1>
        <p className="text-gray-600">
          {t('editListing.updateDetailsFor', 'Update your listing details for')} <span className="font-medium">{cardName}</span>
        </p>
      </div>

      {/* Error Messages */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Bundle Details */}
        {isBundle && (
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-2xl font-bold mb-4">Bundle Details</h2>
            <p className="text-sm text-gray-600 mb-4">
              Update the cards included in this bundle. Price will be treated as the total for the entire bundle.
            </p>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Bundle Name (optional)
              </label>
              <input
                type="text"
                value={bundleName}
                onChange={(e) => setBundleName(e.target.value)}
                placeholder="Starter deck collection..."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {/* Left: search box */}
              <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                <h3 className="text-lg font-semibold mb-3">Search cards to add</h3>
                <input
                  type="text"
                  value={bundleSearchTerm}
                  onChange={(e) => setBundleSearchTerm(e.target.value)}
                  placeholder="Type card name..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
                {bundleSearching && <p className="text-xs text-gray-500 mt-2">Searching...</p>}
                {bundleSearchResults.length > 0 && (
                  <div className="mt-3 max-h-64 overflow-y-auto border border-gray-200 rounded-lg divide-y">
                    {bundleSearchResults.map((card) => (
                      <button
                        key={card.id}
                        type="button"
                        onClick={() => handleAddBundleCard(card)}
                        className="w-full text-left px-3 py-2 hover:bg-white flex items-center gap-3"
                      >
                        {card.imageUrl && (
                          <img src={card.imageUrl} alt={card.name} className="w-12 h-16 object-cover rounded border" />
                        )}
                        <div className="flex-1">
                          <div className="font-medium text-sm">{card.name}</div>
                          <div className="text-xs text-gray-500">#{card.cardNumber || '—'}</div>
                        </div>
                        <span className="text-xs text-indigo-700 font-semibold">Add</span>
                      </button>
                    ))}
                  </div>
                )}
                {bundleSearchTerm.trim().length >= 2 && bundleSearchResults.length === 0 && !bundleSearching && (
                  <div className="mt-3">
                    <button
                      type="button"
                      onClick={handleAddCustomBundleItem}
                      className="w-full px-3 py-2 border border-dashed border-indigo-300 text-indigo-700 rounded-lg hover:bg-indigo-50 text-sm"
                    >
                      Add "{bundleSearchTerm.trim()}" as custom item
                    </button>
                  </div>
                )}
              </div>

              {/* Right: bundle list */}
              <div className="border border-gray-200 rounded-lg p-4">
                <h3 className="text-lg font-semibold mb-3">Bundle Items</h3>
                {bundleItems.length === 0 ? (
                  <p className="text-sm text-gray-500">No items added yet. Search on the left to add cards.</p>
                ) : (
                  <div className="space-y-3">
                    {bundleItems.map((item, index) => (
                      <div key={index} className="flex gap-3 items-start border border-gray-100 rounded-lg p-3">
                        <div className="w-20 h-20 bg-gray-100 rounded overflow-hidden flex items-center justify-center">
                          {item.imageUrl ? (
                            <img src={item.imageUrl} alt={item.name} className="w-full h-full object-cover" />
                          ) : (
                            <div className="text-gray-400 text-2xl">🎴</div>
                          )}
                        </div>
                        <div className="flex-1 space-y-2">
                          <input
                            type="text"
                            value={item.name}
                            onChange={(e) => handleBundleItemChange(index, 'name', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                          />
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="block text-xs font-medium text-gray-600 mb-1">Quantity</label>
                              <input
                                type="number"
                                min="1"
                                value={item.quantity}
                                onChange={(e) => handleBundleItemChange(index, 'quantity', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-600 mb-1">Notes</label>
                              <input
                                type="text"
                                value={item.notes || ''}
                                onChange={(e) => handleBundleItemChange(index, 'notes', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                placeholder="Condition, edition..."
                              />
                            </div>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleRemoveBundleItem(index)}
                          className="text-red-600 hover:text-red-700 px-2"
                          title="Remove from bundle"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Listing Details */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h2 className="text-2xl font-bold mb-4">{t('editListing.listingDetails', 'Listing Details')}</h2>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Price */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('createListing.price', 'Price (THB)')} *
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                placeholder="0.00"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>

            {/* Quantity */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('createListing.quantity', 'Quantity')} *
              </label>
              <input
                type="number"
                min="1"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>

            {/* Description */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('createListing.description', 'Description (Optional)')}
              </label>
              <textarea
                placeholder={t('createListing.descriptionPlaceholder', 'Add any additional details about the card...')}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={4}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex gap-4">
          <button
            type="button"
            onClick={() => navigate(`/marketplace/listing/${id}`)}
            className="flex-1 bg-gray-200 text-gray-700 py-3 rounded-lg hover:bg-gray-300 transition font-medium"
          >
            {t('common.cancel', 'Cancel')}
          </button>
          <button
            type="submit"
            disabled={loading}
            className="flex-1 bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 transition font-medium disabled:opacity-50"
          >
            {loading ? t('common.updating', 'Updating...') : t('editListing.updateListing', 'Update Listing')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditListingPage;
